import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { branding as brandingApi, resolveAssetUrl } from '../utils/api';
import { useAuth } from '../utils/AuthContext';
import SocialPlatformIcon from '../components/SocialPlatformIcon';
import SocialConnections from '../components/SocialConnections';
import FlyerContactPreferences from '../components/FlyerContactPreferences';
import toast from 'react-hot-toast';
import { 
  Upload, 
  Palette, 
  Save, 
  Trash2, 
  Image,
  Building2,
  User,
  Phone,
  MapPin,
  Globe,
  Loader,
  Check,
  ArrowLeft,
  ArrowRight,
  Sparkles,
  CheckCircle2
} from 'lucide-react';

const SOCIAL_PLATFORMS = [
  { key: 'instagram', label: 'Instagram', placeholder: 'yourcompany' },
  { key: 'facebook', label: 'Facebook', placeholder: 'yourcompany' },
  { key: 'linkedin', label: 'LinkedIn', placeholder: 'your-company' },
  { key: 'x', label: 'X', placeholder: 'yourcompany' }
];

const DEFAULT_MAX_LOGO_UPLOAD_MB = 25;
const BYTES_PER_MEGABYTE = 1024 * 1024;

const CompanyWordmarkPreview = ({ companyName, primaryColor, secondaryColor, compact = false }) => (
  <div
    className={`w-full flex flex-col items-center justify-center ${compact ? 'min-h-[8rem]' : 'min-h-[10rem]'}`}
    aria-label={`${companyName || 'Your Company'} text logo preview`}
  >
    <div
      className={`${compact ? 'text-2xl' : 'text-3xl sm:text-4xl'} font-extrabold tracking-tight leading-tight text-center break-words max-w-full px-3`}
      style={{ color: primaryColor }}
    >
      {companyName || 'Your Company'}
    </div>
    <div className="mt-3 h-1.5 w-2/5 min-w-24 rounded-full" style={{ backgroundColor: secondaryColor }}></div>
  </div>
);

const canvasToBlob = (canvas, type, quality) => new Promise((resolve, reject) => {
  canvas.toBlob(blob => {
    if (blob) resolve(blob);
    else reject(new Error('The browser could not optimize this image'));
  }, type, quality);
});

const optimizeOversizedLogo = async (file, maxBytes) => {
  if (file.size <= maxBytes) return { file, optimized: false };

  const objectUrl = window.URL.createObjectURL(file);
  try {
    const image = await new Promise((resolve, reject) => {
      const element = new window.Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('The selected logo could not be opened'));
      element.src = objectUrl;
    });

    const maxDimension = 2000;
    const scale = Math.min(1, maxDimension / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    const context = canvas.getContext('2d');
    if (!context) throw new Error('The browser could not optimize this image');
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    const optimizedBlob = await canvasToBlob(canvas, 'image/webp', 0.9);
    if (optimizedBlob.size > maxBytes) {
      throw new Error(`This logo is still over ${Math.round(maxBytes / BYTES_PER_MEGABYTE)}MB after automatic optimization`);
    }

    const filename = file.name.replace(/\.[^.]+$/, '') || 'company-logo';
    return {
      file: new File([optimizedBlob], `${filename}-optimized.webp`, { type: 'image/webp' }),
      optimized: true
    };
  } finally {
    window.URL.revokeObjectURL(objectUrl);
  }
};

const Branding = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fileInputRef = useRef(null);
  const logoDragDepthRef = useRef(0);
  
  const [brandingData, setBrandingData] = useState({
    logoUrl: '',
    logoMode: 'uploaded',
    primaryColor: '#1E3A5F',
    secondaryColor: '#4A90A4',
    companyName: '',
    contactName: '',
    phone: '',
    address: '',
    website: '',
    displayEmail: '',
    flyerContactVisibility: {},
    socialHandles: {
      instagram: '',
      facebook: '',
      linkedin: '',
      x: ''
    }
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [isLogoDragActive, setIsLogoDragActive] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(1);
  const [colorSuggestion, setColorSuggestion] = useState(null);
  const [maxLogoUploadMb, setMaxLogoUploadMb] = useState(DEFAULT_MAX_LOGO_UPLOAD_MB);
  const isOnboarding = searchParams.get('onboarding') === '1'
    || user?.branding?.onboardingComplete === false;

  const goToOnboardingStep = (step) => {
    setOnboardingStep(step);
    window.requestAnimationFrame(() => window.scrollTo({ top: 0, behavior: 'auto' }));
  };

  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await brandingApi.get();
        setBrandingData({
          logoUrl: response.data.logoUrl || '',
          logoMode: response.data.logoMode || 'uploaded',
          primaryColor: response.data.primaryColor || '#1E3A5F',
          secondaryColor: response.data.secondaryColor || '#4A90A4',
          companyName: response.data.companyName || '',
          contactName: response.data.contactName || '',
          phone: response.data.phone || '',
          address: response.data.address || '',
          website: response.data.website || '',
          displayEmail: response.data.displayEmail ?? response.data.email ?? user?.email ?? '',
          flyerContactVisibility: response.data.flyerContactVisibility || {},
          socialHandles: {
            instagram: response.data.socialHandles?.instagram || '',
            facebook: response.data.socialHandles?.facebook || '',
            linkedin: response.data.socialHandles?.linkedin || '',
            x: response.data.socialHandles?.x || ''
          }
        });
        setMaxLogoUploadMb(response.data.maxLogoUploadMb || DEFAULT_MAX_LOGO_UPLOAD_MB);
      } catch (error) {
        console.error('Error fetching branding:', error);
        toast.error('Failed to load branding data');
      } finally {
        setLoading(false);
      }
    };
    fetchBranding();
  }, [user?.email]);

  const uploadLogoFile = async (file) => {
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(file.type)) {
      toast.error('Logo must be a PNG, JPG, GIF, or WebP image');
      return;
    }

    setUploadingLogo(true);
    try {
      const preparedLogo = await optimizeOversizedLogo(
        file,
        maxLogoUploadMb * BYTES_PER_MEGABYTE
      );
      const response = await brandingApi.uploadLogo(preparedLogo.file);
      const suggestion = response.data.suggestedColors;
      setBrandingData(prev => ({
        ...prev,
        logoUrl: response.data.logoUrl,
        logoMode: 'uploaded',
        primaryColor: suggestion?.primaryColor || prev.primaryColor,
        secondaryColor: suggestion?.secondaryColor || prev.secondaryColor
      }));
      setColorSuggestion(suggestion || null);
      updateUser({
        branding: {
          ...(user?.branding || {}),
          logoUrl: response.data.logoUrl,
          logoMode: 'uploaded',
          primaryColor: suggestion?.primaryColor || brandingData.primaryColor,
          secondaryColor: suggestion?.secondaryColor || brandingData.secondaryColor,
          onboardingComplete: isOnboarding ? false : user?.branding?.onboardingComplete === true
        }
      });
      if (isOnboarding) goToOnboardingStep(2);
      if (suggestion) {
        toast.success(preparedLogo.optimized
          ? 'Logo optimized and uploaded. Brand colors applied automatically!'
          : 'Logo uploaded and brand colors applied automatically!');
      } else {
        toast.success(preparedLogo.optimized ? 'Logo optimized and uploaded!' : 'Logo uploaded successfully!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.error || error.message || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const input = event.target;
    await uploadLogoFile(input.files?.[0]);
    input.value = '';
  };

  const handleLogoDragEnter = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (uploadingLogo) return;
    logoDragDepthRef.current += 1;
    setIsLogoDragActive(true);
  };

  const handleLogoDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
  };

  const handleLogoDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    logoDragDepthRef.current = Math.max(0, logoDragDepthRef.current - 1);
    if (logoDragDepthRef.current === 0) setIsLogoDragActive(false);
  };

  const handleLogoDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    logoDragDepthRef.current = 0;
    setIsLogoDragActive(false);
    if (uploadingLogo) return;
    await uploadLogoFile(event.dataTransfer?.files?.[0]);
  };

  const handleDeleteLogo = async () => {
    if (!window.confirm('Are you sure you want to delete your logo?')) return;

    try {
      await brandingApi.deleteLogo();
      setBrandingData(prev => ({ ...prev, logoUrl: '', logoMode: 'uploaded' }));
      setColorSuggestion(null);
      updateUser({
        branding: {
          ...(user?.branding || {}),
          logoUrl: null,
          logoMode: 'uploaded',
          onboardingComplete: false
        }
      });
      toast.success('Logo deleted');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete logo');
    }
  };

  const handleUseWordmark = async () => {
    if (brandingData.logoUrl && !window.confirm('Replace your uploaded logo with a company-name text logo? The uploaded logo file will be removed.')) {
      return;
    }

    setSaving(true);
    try {
      const response = await brandingApi.useCompanyWordmark();
      setBrandingData(prev => ({
        ...prev,
        logoUrl: '',
        logoMode: 'wordmark',
        primaryColor: response.data.primaryColor || prev.primaryColor,
        secondaryColor: response.data.secondaryColor || prev.secondaryColor
      }));
      setColorSuggestion(null);
      updateUser({
        branding: {
          ...(user?.branding || {}),
          logoUrl: null,
          logoMode: 'wordmark',
          primaryColor: response.data.primaryColor || brandingData.primaryColor,
          secondaryColor: response.data.secondaryColor || brandingData.secondaryColor,
          onboardingComplete: user?.branding?.onboardingComplete === true
        }
      });
      toast.success('Your company name will be used as a professional text logo.');
      if (isOnboarding) goToOnboardingStep(2);
    } catch (error) {
      console.error('Text logo error:', error);
      toast.error(error.response?.data?.error || 'Failed to set up your text logo');
    } finally {
      setSaving(false);
    }
  };

  const saveColors = async ({ showToast = true } = {}) => {
    setSaving(true);
    try {
      await brandingApi.updateColors(brandingData.primaryColor, brandingData.secondaryColor);
      updateUser({
        branding: {
          ...(user?.branding || {}),
          logoUrl: brandingData.logoUrl,
          logoMode: brandingData.logoMode,
          primaryColor: brandingData.primaryColor,
          secondaryColor: brandingData.secondaryColor,
          onboardingComplete: user?.branding?.onboardingComplete === true
        }
      });
      if (showToast) toast.success('Colors saved!');
      return true;
    } catch (error) {
      console.error('Save colors error:', error);
      toast.error('Failed to save colors');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveColors = () => saveColors();

  const handleSaveColorsAndContinue = async () => {
    if (await saveColors({ showToast: false })) {
      goToOnboardingStep(3);
    }
  };

  const saveProfile = async ({ showToast = true, manageSaving = true } = {}) => {
    if (manageSaving) setSaving(true);
    try {
      const response = await brandingApi.updateProfile({
        companyName: brandingData.companyName,
        contactName: brandingData.contactName,
        phone: brandingData.phone,
        address: brandingData.address,
        website: brandingData.website,
        socialHandles: brandingData.socialHandles,
        displayEmail: brandingData.displayEmail.trim() === user?.email ? '' : brandingData.displayEmail,
        flyerContactVisibility: brandingData.flyerContactVisibility
      });
      updateUser({
        companyName: brandingData.companyName,
        contactName: brandingData.contactName,
        phone: brandingData.phone,
        address: brandingData.address,
        website: brandingData.website,
        socialHandles: brandingData.socialHandles,
        displayEmail: response.data.displayEmail ?? null,
        flyerContactVisibility: response.data.flyerContactVisibility || brandingData.flyerContactVisibility
      });
      if (showToast) toast.success('Profile saved!');
      return true;
    } catch (error) {
      console.error('Save profile error:', error);
      toast.error(error.response?.data?.error || 'Failed to save profile');
      return false;
    } finally {
      if (manageSaving) setSaving(false);
    }
  };

  const handleSaveProfile = () => saveProfile();

  const handleFinishOnboarding = async () => {
    if (!brandingData.logoUrl && brandingData.logoMode !== 'wordmark') {
      goToOnboardingStep(1);
      toast.error('Upload your company logo or choose the text-logo option to continue');
      return;
    }

    setSaving(true);
    try {
      await brandingApi.updateColors(brandingData.primaryColor, brandingData.secondaryColor);
      const profileSaved = await saveProfile({ showToast: false, manageSaving: false });
      if (!profileSaved) return;
      await brandingApi.completeOnboarding();
      updateUser({
        companyName: brandingData.companyName,
        contactName: brandingData.contactName,
        phone: brandingData.phone,
        address: brandingData.address,
        website: brandingData.website,
        socialHandles: brandingData.socialHandles,
        displayEmail: brandingData.displayEmail.trim() === user?.email ? null : brandingData.displayEmail.trim() || null,
        flyerContactVisibility: brandingData.flyerContactVisibility,
        branding: {
          logoUrl: brandingData.logoUrl,
          logoMode: brandingData.logoMode,
          primaryColor: brandingData.primaryColor,
          secondaryColor: brandingData.secondaryColor,
          onboardingComplete: true
        }
      });
      toast.success('Your brand is ready!');
      navigate('/flyers', { replace: true });
    } catch (error) {
      console.error('Complete onboarding error:', error);
      toast.error(error.response?.data?.error || 'Failed to complete brand setup');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (isOnboarding) {
    const steps = [
      { number: 1, label: 'Logo' },
      { number: 2, label: 'Colors' },
      { number: 3, label: 'Contact & Socials' }
    ];

    return (
      <div className="animate-fade-in max-w-4xl mx-auto">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary-50 text-primary-700 rounded-full text-sm font-semibold mb-4">
            <Sparkles size={16} /> New account setup
          </div>
          <h1 className="text-3xl font-bold text-gray-900 font-display">Set up your brand</h1>
          <p className="text-gray-600 mt-2">
            Add your logo—or use a polished company-name wordmark—then review your colors and optional social handles before opening the flyer library.
          </p>
        </div>

        <ol className="grid grid-cols-3 gap-2 mb-8" aria-label="Brand setup progress">
          {steps.map(step => {
            const isComplete = onboardingStep > step.number;
            const isCurrent = onboardingStep === step.number;
            return (
              <li
                key={step.number}
                className={`rounded-xl border px-3 py-3 flex items-center gap-3 ${
                  isCurrent
                    ? 'border-primary-300 bg-primary-50 text-primary-800'
                    : isComplete
                      ? 'border-green-200 bg-green-50 text-green-800'
                      : 'border-gray-200 bg-white text-gray-400'
                }`}
              >
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                  isCurrent ? 'bg-primary-500 text-white' : isComplete ? 'bg-green-500 text-white' : 'bg-gray-100'
                }`}>
                  {isComplete ? <Check size={15} /> : step.number}
                </span>
                <span className="text-sm font-semibold hidden sm:block">{step.label}</span>
              </li>
            );
          })}
        </ol>

        {onboardingStep === 1 && (
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Image size={22} className="text-primary-500" /> Add your company logo
              </h2>
              <p className="text-sm text-gray-500 mt-2">
                Uploading your logo is recommended and remains the default. A transparent PNG usually looks best.
              </p>
            </div>

            <div
              className={`border-2 border-dashed rounded-2xl p-8 text-center transition ${isLogoDragActive ? 'border-primary-500 bg-primary-50 ring-4 ring-primary-100' : 'border-gray-200 bg-gray-50/60'}`}
              onDragEnter={handleLogoDragEnter}
              onDragOver={handleLogoDragOver}
              onDragLeave={handleLogoDragLeave}
              onDrop={handleLogoDrop}
              aria-label="Logo upload drop zone"
            >
              {brandingData.logoUrl ? (
                <div className="space-y-5">
                  <div className="bg-white rounded-xl border border-gray-100 p-5 max-w-md mx-auto">
                    <img
                      src={resolveAssetUrl(brandingData.logoUrl)}
                      alt="Company logo"
                      className="max-h-40 max-w-full mx-auto object-contain"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="btn-outline text-sm px-4 py-2"
                    disabled={uploadingLogo}
                  >
                    Change Logo
                  </button>
                  <p className="text-xs text-gray-500">or drag and drop a replacement logo here</p>
                </div>
              ) : brandingData.logoMode === 'wordmark' ? (
                <div className="space-y-5">
                  <div className="bg-white rounded-xl border border-gray-100 p-5 max-w-xl mx-auto shadow-sm">
                    <CompanyWordmarkPreview
                      companyName={brandingData.companyName}
                      primaryColor={brandingData.primaryColor}
                      secondaryColor={brandingData.secondaryColor}
                    />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">Company-name text logo selected</p>
                    <p className="text-sm text-gray-500 mt-1">It will automatically resize to fit each flyer’s logo area.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="btn-outline text-sm px-4 py-2"
                    disabled={uploadingLogo}
                  >
                    Upload a logo instead
                  </button>
                  <p className="text-xs text-gray-500">or drag and drop a logo here</p>
                </div>
              ) : (
                <button
                  type="button"
                  className="cursor-pointer w-full py-4"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingLogo}
                >
                  {uploadingLogo ? (
                    <Loader className="mx-auto text-primary-500 animate-spin mb-3" size={44} />
                  ) : (
                    <Upload className="mx-auto text-gray-400 mb-3" size={44} />
                  )}
                  <p className="font-semibold text-gray-800">
                    {uploadingLogo ? 'Uploading and analyzing your logo...' : isLogoDragActive ? 'Drop your logo here' : 'Drag and drop your logo here'}
                  </p>
                  <p className="text-sm text-gray-500 mt-1">or click to browse. PNG, JPG, GIF, or WebP; large files are optimized automatically.</p>
                </button>
              )}
            </div>

            {brandingData.logoMode !== 'wordmark' && (
              <div className="mt-4 text-center">
                <button
                  type="button"
                  onClick={handleUseWordmark}
                  disabled={saving || uploadingLogo}
                  className="text-sm font-medium text-gray-500 hover:text-primary-600 underline underline-offset-4 disabled:opacity-50"
                >
                  I don’t have a logo — use my company name instead
                </button>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleLogoUpload}
              className="hidden"
            />

            <div className="flex justify-end mt-6">
              <button
                type="button"
                onClick={() => goToOnboardingStep(2)}
                disabled={(!brandingData.logoUrl && brandingData.logoMode !== 'wordmark') || uploadingLogo}
                className="btn-primary flex items-center justify-center gap-2"
              >
                Continue to Colors <ArrowRight size={18} />
              </button>
            </div>
          </section>
        )}

        {onboardingStep === 2 && (
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Palette size={22} className="text-primary-500" /> Review your brand colors
              </h2>
              <p className="text-sm text-gray-500 mt-2">
                {brandingData.logoMode === 'wordmark'
                  ? 'We started you with a clean navy-and-teal palette. Keep it or choose any colors you prefer.'
                  : 'We analyze the visible colors in your logo and automatically apply a coordinated pair. Adjust either color if you prefer.'}
              </p>
            </div>

            {colorSuggestion && (
              <div className="rounded-xl border border-green-200 bg-green-50 p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center gap-2 text-green-800 font-semibold text-sm flex-1">
                  <CheckCircle2 size={19} /> Suggested from your logo
                </div>
                <div className="flex items-center gap-2" aria-label="Colors found in your logo">
                  {colorSuggestion.swatches?.map(color => (
                    <span
                      key={color}
                      className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                      style={{ backgroundColor: color }}
                      title={color}
                    />
                  ))}
                </div>
              </div>
            )}

            {brandingData.logoMode === 'wordmark' && !colorSuggestion && (
              <div className="rounded-xl border border-primary-100 bg-primary-50 p-4 mb-6 flex items-center gap-2 text-primary-800 font-semibold text-sm">
                <Sparkles size={19} /> Professional starter palette applied
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="onboarding-primary-color" className="block text-sm font-medium text-gray-700 mb-2">Primary Color</label>
                <div className="flex items-center gap-3">
                  <input
                    id="onboarding-primary-color"
                    type="color"
                    value={brandingData.primaryColor}
                    onChange={(e) => setBrandingData(prev => ({ ...prev, primaryColor: e.target.value }))}
                    className="w-16 h-12 rounded-lg cursor-pointer border-2 border-gray-200"
                  />
                  <input
                    aria-label="Primary color hex value"
                    type="text"
                    value={brandingData.primaryColor}
                    onChange={(e) => setBrandingData(prev => ({ ...prev, primaryColor: e.target.value }))}
                    className="input-field flex-1"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="onboarding-secondary-color" className="block text-sm font-medium text-gray-700 mb-2">Secondary Color</label>
                <div className="flex items-center gap-3">
                  <input
                    id="onboarding-secondary-color"
                    type="color"
                    value={brandingData.secondaryColor}
                    onChange={(e) => setBrandingData(prev => ({ ...prev, secondaryColor: e.target.value }))}
                    className="w-16 h-12 rounded-lg cursor-pointer border-2 border-gray-200"
                  />
                  <input
                    aria-label="Secondary color hex value"
                    type="text"
                    value={brandingData.secondaryColor}
                    onChange={(e) => setBrandingData(prev => ({ ...prev, secondaryColor: e.target.value }))}
                    className="input-field flex-1"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 mt-6">
              <div
                className="h-24 rounded-xl flex items-center justify-center text-white font-semibold shadow-inner"
                style={{ backgroundColor: brandingData.primaryColor }}
              >
                Primary
              </div>
              <div
                className="h-24 rounded-xl flex items-center justify-center text-white font-semibold shadow-inner"
                style={{ backgroundColor: brandingData.secondaryColor }}
              >
                Secondary
              </div>
            </div>

            <div className="flex items-center justify-between gap-3 mt-6">
              <button type="button" onClick={() => goToOnboardingStep(1)} className="btn-outline flex items-center gap-2">
                <ArrowLeft size={18} /> Back
              </button>
              <button
                type="button"
                onClick={handleSaveColorsAndContinue}
                disabled={saving}
                className="btn-primary flex items-center justify-center gap-2"
              >
                {saving ? <Loader className="animate-spin" size={18} /> : <ArrowRight size={18} />}
                Save Colors & Continue
              </button>
            </div>
          </section>
        )}

        {onboardingStep === 3 && (
          <section className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <Building2 size={22} className="text-primary-500" /> Confirm your contact information
              </h2>
              <p className="text-sm text-gray-500 mt-2">
                Choose your public contact details below. Your account email stays separate from the email you display on flyers.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="onboarding-company" className="block text-sm font-medium text-gray-700 mb-2">Company Name *</label>
                <input id="onboarding-company" type="text" value={brandingData.companyName} onChange={(e) => setBrandingData(prev => ({ ...prev, companyName: e.target.value }))} className="input-field" required />
              </div>
              <div>
                <label htmlFor="onboarding-contact" className="block text-sm font-medium text-gray-700 mb-2">Contact Name</label>
                <input id="onboarding-contact" type="text" value={brandingData.contactName} onChange={(e) => setBrandingData(prev => ({ ...prev, contactName: e.target.value }))} className="input-field" />
              </div>
              <div>
                <label htmlFor="onboarding-phone" className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                <input id="onboarding-phone" type="tel" value={brandingData.phone} onChange={(e) => setBrandingData(prev => ({ ...prev, phone: e.target.value }))} className="input-field" />
              </div>
              <div>
                <label htmlFor="onboarding-website" className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                <input id="onboarding-website" type="text" value={brandingData.website} onChange={(e) => setBrandingData(prev => ({ ...prev, website: e.target.value }))} className="input-field" />
              </div>
              <div className="md:col-span-2">
                <label htmlFor="onboarding-address" className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                <input id="onboarding-address" type="text" value={brandingData.address} onChange={(e) => setBrandingData(prev => ({ ...prev, address: e.target.value }))} className="input-field" />
              </div>
            </div>

            <FlyerContactPreferences
              accountEmail={user?.email || ''}
              displayEmail={brandingData.displayEmail}
              visibility={brandingData.flyerContactVisibility}
              onEmailChange={displayEmail => setBrandingData(prev => ({ ...prev, displayEmail }))}
              onVisibilityChange={(field, show) => setBrandingData(prev => ({ ...prev, flyerContactVisibility: { ...prev.flyerContactVisibility, [field]: show } }))}
            />

            <div className="mt-7 pt-6 border-t border-gray-100">
              <h3 className="font-semibold text-gray-900">Social Handles <span className="font-normal text-gray-400">(Optional)</span></h3>
              <p className="text-sm text-gray-500 mt-1 mb-4">Enter handles only—never passwords.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {SOCIAL_PLATFORMS.map(platform => (
                  <div key={platform.key}>
                    <label htmlFor={`onboarding-social-${platform.key}`} className="block text-sm font-medium text-gray-700 mb-2">{platform.label}</label>
                    <div className="relative">
                      <SocialPlatformIcon platform={platform.key} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                      <span className="absolute left-10 top-1/2 -translate-y-1/2 text-gray-400 text-sm">@</span>
                      <input
                        id={`onboarding-social-${platform.key}`}
                        type="text"
                        value={brandingData.socialHandles[platform.key]}
                        onChange={(e) => setBrandingData(prev => ({
                          ...prev,
                          socialHandles: {
                            ...prev.socialHandles,
                            [platform.key]: e.target.value.replace(/^@+/, '')
                          }
                        }))}
                        className="input-field pl-16"
                        placeholder={platform.placeholder}
                        maxLength={80}
                        autoCapitalize="none"
                        autoCorrect="off"
                        spellCheck="false"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 mt-7">
              <button type="button" onClick={() => goToOnboardingStep(2)} className="btn-outline flex items-center justify-center gap-2">
                <ArrowLeft size={18} /> Back
              </button>
              <button
                type="button"
                onClick={handleFinishOnboarding}
                disabled={saving}
                className="btn-primary flex items-center justify-center gap-2"
              >
                {saving ? <Loader className="animate-spin" size={18} /> : <CheckCircle2 size={18} />}
                Finish & View Flyers
              </button>
            </div>
          </section>
        )}
      </div>
    );
  }

  return (
    <div className="animate-fade-in max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 font-display">My Branding</h1>
        <p className="text-gray-600 mt-2">Customize how your flyers look with your company branding</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Logo Upload */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Image size={20} className="text-primary-500" />
            Company Logo
          </h2>
          
          <div
            className={`border-2 border-dashed rounded-xl p-6 text-center transition ${isLogoDragActive ? 'border-primary-500 bg-primary-50 ring-4 ring-primary-100' : 'border-gray-200'}`}
            onDragEnter={handleLogoDragEnter}
            onDragOver={handleLogoDragOver}
            onDragLeave={handleLogoDragLeave}
            onDrop={handleLogoDrop}
            aria-label="Logo upload drop zone"
          >
            {brandingData.logoUrl ? (
              <div className="space-y-4">
                <img 
                  src={resolveAssetUrl(brandingData.logoUrl)}
                  alt="Company logo"
                  className="max-h-32 mx-auto object-contain"
                />
                <div className="flex justify-center gap-3">
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="btn-outline text-sm px-4 py-2"
                    disabled={uploadingLogo}
                  >
                    Change Logo
                  </button>
                  <button
                    onClick={handleDeleteLogo}
                    className="text-red-500 hover:text-red-600 px-4 py-2 text-sm font-medium"
                  >
                    <Trash2 size={16} className="inline mr-1" />
                    Remove
                  </button>
                </div>
                <p className="text-xs text-gray-500">or drag and drop a replacement logo here</p>
              </div>
            ) : brandingData.logoMode === 'wordmark' ? (
              <div className="space-y-4">
                <CompanyWordmarkPreview
                  companyName={brandingData.companyName}
                  primaryColor={brandingData.primaryColor}
                  secondaryColor={brandingData.secondaryColor}
                  compact
                />
                <div>
                  <p className="text-sm font-semibold text-gray-700">Company-name text logo</p>
                  <p className="text-xs text-gray-500 mt-1">Updates automatically with your name and colors.</p>
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="btn-outline text-sm px-4 py-2"
                  disabled={uploadingLogo}
                >
                  Upload a logo instead
                </button>
                <p className="text-xs text-gray-500">or drag and drop a logo here</p>
              </div>
            ) : (
              <button
                type="button"
                className="cursor-pointer w-full"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingLogo}
              >
                {uploadingLogo ? (
                  <Loader className="mx-auto text-primary-500 animate-spin mb-3" size={40} />
                ) : (
                  <Upload className="mx-auto text-gray-400 mb-3" size={40} />
                )}
                <p className="font-medium text-gray-700">
                  {uploadingLogo ? 'Uploading...' : isLogoDragActive ? 'Drop your logo here' : 'Drag and drop your logo here'}
                </p>
                <p className="text-sm text-gray-500 mt-1">or click to browse. PNG, JPG, GIF, or WebP; large files are optimized automatically.</p>
              </button>
            )}
          </div>

          {brandingData.logoMode !== 'wordmark' && (
            <button
              type="button"
              onClick={handleUseWordmark}
              disabled={saving || uploadingLogo}
              className="mt-4 w-full text-sm font-medium text-gray-500 hover:text-primary-600 underline underline-offset-4 disabled:opacity-50"
            >
              I don’t have a logo — use my company name instead
            </button>
          )}
          
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleLogoUpload}
            className="hidden"
          />
        </div>

        {/* Color Selection */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Palette size={20} className="text-primary-500" />
            Brand Colors
          </h2>

          {colorSuggestion && (
            <div className="rounded-lg border border-green-200 bg-green-50 px-3 py-3 mb-4">
              <div className="flex items-center justify-between gap-3">
                <span className="text-sm font-semibold text-green-800 flex items-center gap-2">
                  <Sparkles size={16} /> Suggested from your logo
                </span>
                <div className="flex items-center gap-1.5">
                  {colorSuggestion.swatches?.slice(0, 4).map(color => (
                    <span key={color} className="w-6 h-6 rounded-full border-2 border-white shadow-sm" style={{ backgroundColor: color }} title={color} />
                  ))}
                </div>
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Primary Color</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={brandingData.primaryColor}
                  onChange={(e) => setBrandingData(prev => ({ ...prev, primaryColor: e.target.value }))}
                  className="w-16 h-10 rounded-lg cursor-pointer border-2 border-gray-200"
                />
                <input
                  type="text"
                  value={brandingData.primaryColor}
                  onChange={(e) => setBrandingData(prev => ({ ...prev, primaryColor: e.target.value }))}
                  className="input-field flex-1"
                  placeholder="#1E3A5F"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Secondary Color</label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={brandingData.secondaryColor}
                  onChange={(e) => setBrandingData(prev => ({ ...prev, secondaryColor: e.target.value }))}
                  className="w-16 h-10 rounded-lg cursor-pointer border-2 border-gray-200"
                />
                <input
                  type="text"
                  value={brandingData.secondaryColor}
                  onChange={(e) => setBrandingData(prev => ({ ...prev, secondaryColor: e.target.value }))}
                  className="input-field flex-1"
                  placeholder="#4A90A4"
                />
              </div>
            </div>

            {/* Preview */}
            <div className="pt-4 border-t border-gray-100">
              <label className="block text-sm font-medium text-gray-700 mb-2">Preview</label>
              <div className="flex gap-2">
                <div 
                  className="flex-1 h-16 rounded-lg flex items-center justify-center text-white text-sm font-medium"
                  style={{ backgroundColor: brandingData.primaryColor }}
                >
                  Primary
                </div>
                <div 
                  className="flex-1 h-16 rounded-lg flex items-center justify-center text-white text-sm font-medium"
                  style={{ backgroundColor: brandingData.secondaryColor }}
                >
                  Secondary
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={handleSaveColors}
            disabled={saving}
            className="btn-primary w-full mt-6 flex items-center justify-center gap-2"
          >
            {saving ? <Loader className="animate-spin" size={18} /> : <Check size={18} />}
            Save Colors
          </button>
        </div>

        {/* Contact Information */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 lg:col-span-2">
          <h2 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <Building2 size={20} className="text-primary-500" />
            Contact Information
          </h2>
          <p className="text-sm text-gray-500 mb-6">
            Save your contact details here, then choose what to display on your flyers below.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
              <div className="relative">
                <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  value={brandingData.companyName}
                  onChange={(e) => setBrandingData(prev => ({ ...prev, companyName: e.target.value }))}
                  className="input-field pl-11"
                  placeholder="Your Company Name"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contact Name</label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  value={brandingData.contactName}
                  onChange={(e) => setBrandingData(prev => ({ ...prev, contactName: e.target.value }))}
                  className="input-field pl-11"
                  placeholder="John Smith"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="tel"
                  value={brandingData.phone}
                  onChange={(e) => setBrandingData(prev => ({ ...prev, phone: e.target.value }))}
                  className="input-field pl-11"
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  value={brandingData.address}
                  onChange={(e) => setBrandingData(prev => ({ ...prev, address: e.target.value }))}
                  className="input-field pl-11"
                  placeholder="123 Main St, City, ST 12345"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
              <div className="relative">
                <Globe className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  value={brandingData.website}
                  onChange={(e) => setBrandingData(prev => ({ ...prev, website: e.target.value }))}
                  className="input-field pl-11"
                  placeholder="www.yourcompany.com"
                />
              </div>
            </div>
          </div>

          <FlyerContactPreferences
            accountEmail={user?.email || ''}
            displayEmail={brandingData.displayEmail}
            visibility={brandingData.flyerContactVisibility}
            onEmailChange={displayEmail => setBrandingData(prev => ({ ...prev, displayEmail }))}
            onVisibilityChange={(field, show) => setBrandingData(prev => ({ ...prev, flyerContactVisibility: { ...prev.flyerContactVisibility, [field]: show } }))}
          />

          <div className="mt-6 pt-6 border-t border-gray-100">
            <h3 className="font-semibold text-gray-800">Social Handles <span className="font-normal text-gray-400">(Optional)</span></h3>
            <p className="text-sm text-gray-500 mt-1 mb-4">
              Add handles only—no passwords or account connections. Flyers hide this row automatically when every field is blank.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {SOCIAL_PLATFORMS.map((platform) => (
                <div key={platform.key}>
                  <label htmlFor={`social-${platform.key}`} className="block text-sm font-medium text-gray-700 mb-2">
                    {platform.label}
                  </label>
                  <div className="relative">
                    <SocialPlatformIcon
                      platform={platform.key}
                      className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
                    />
                    <span className="absolute left-10 top-1/2 -translate-y-1/2 text-gray-400 text-sm">@</span>
                    <input
                      id={`social-${platform.key}`}
                      type="text"
                      value={brandingData.socialHandles[platform.key]}
                      onChange={(e) => setBrandingData(prev => ({
                        ...prev,
                        socialHandles: {
                          ...prev.socialHandles,
                          [platform.key]: e.target.value.replace(/^@+/, '')
                        }
                      }))}
                      className="input-field pl-16"
                      placeholder={platform.placeholder}
                      maxLength={80}
                      autoCapitalize="none"
                      autoCorrect="off"
                      spellCheck="false"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <button
            onClick={handleSaveProfile}
            disabled={saving}
            className="btn-primary mt-6 flex items-center justify-center gap-2"
          >
            {saving ? <Loader className="animate-spin" size={18} /> : <Save size={18} />}
            Save Profile
          </button>
        </div>
      </div>

      <div className="mt-6">
        <SocialConnections />
      </div>
    </div>
  );
};

export default Branding;
